import { useState, useMemo, useCallback, useEffect } from 'react';
import * as S from './styles';
import { Modal } from '@/components';
import { useClientHierarchy } from '@/hooks';
import { useUserContext } from '@/hooks';

import { useBackNavigation } from '@/shared';

const CoordinatorModal = ({ children, forceOpen = false }: any) => {
    const { hierarchy, handleRCALogin, setViewType } = useClientHierarchy();
    const { customer } = useUserContext();
    const { goBack } = useBackNavigation();

    const [filter, setFilter] = useState('');
    const [isOpenedModalCoordinator, setIsOpenedModalCoordinator] = useState(forceOpen); // Usa forceOpen para abrir diretamente
    const [selectedRCA, setSelectedRCA] = useState<any | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const filteredHierarchy = useMemo(() => {
        if (!filter || !hierarchy) return hierarchy;
        const lowerCaseFilter = filter.toLowerCase();
        return hierarchy.filter((item) =>
            item.nome_usuario.toLowerCase().includes(lowerCaseFilter) ||
            String(item.cod_loja).includes(filter) ||
            String(item.cod_pessoa).includes(filter) ||
            String(item.cod_segmento).includes(filter)
        );
    }, [filter, hierarchy]);

    useEffect(() => {
        if (forceOpen === true) {
            setViewType(true);
        } else {
            setViewType(false);
        }
    }, [forceOpen]);

    const isSelected = useCallback((id: number) => {
        return selectedRCA?.cod_pessoa === id;
    }, [selectedRCA]);

    const handleStartLogin = async () => {
        if (!selectedRCA) return; // Previne erro caso nenhum RCA tenha sido selecionado

        setIsLoading(true); // Ativa o loading no botão

        try {
            await handleRCALogin(selectedRCA); // Aguarda o login concluir completamente
            await new Promise((resolve) => setTimeout(resolve, 300)); // Tempo extra para garantir sincronização
            
        } catch (error) {
            console.error("Erro ao entrar como RCA:", error);
        } finally {
            setIsLoading(false); // Garante que o estado de loading seja desativado em qualquer cenário
            closeModal(); // Fecha o modal após a sincronização
        }
    };

    const closeModal = useCallback(() => {
        if (!forceOpen) {
            setIsOpenedModalCoordinator(false);
        } else {
            goBack();
        }
    }, [forceOpen, goBack]);

    return (
        <>
            {!forceOpen && (
                <S.ButtonSpan onClick={() => setIsOpenedModalCoordinator(true)} data-test="modal-companies-button-open">
                    {children}
                </S.ButtonSpan>
            )}

            <Modal
                id="coordinator-modal"
                fullHeight
                isOpen={isOpenedModalCoordinator}
                closeModal={() => { if (!isLoading) closeModal(); }} // Impede fechamento durante o carregamento
                data-test="coordinator-modal"
            >
                <S.ModalTitle>Olá, {customer?.firstname} {customer?.lastname || ''}</S.ModalTitle>

                <S.InputContent>
                    <S.SearchIcon $isgray size={24} />
                    <S.InputModal
                        placeholder="Busque por nome, loja, pessoa ou segmento"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </S.InputContent>

                <S.ModalContent>
                    <S.CoordinatorInfo>
                        <p>Todos os RCAs de: </p>
                        <strong>{customer?.firstname}</strong>
                    </S.CoordinatorInfo>

                    <S.ContentListCompany>
                        {filteredHierarchy?.map((item) => (
                            <S.RCACard key={item.cod_pessoa} onClick={() => setSelectedRCA(item)}>
                                <S.CheboxContent $isdisabled={false}>
                                    {isSelected(item?.cod_pessoa) ? (
                                        <S.CheckboxCircle />
                                    ) : (
                                        <S.CheckboxBlankCircle />
                                    )}
                                </S.CheboxContent>
                                <S.RCABottomInfo>
                                    <S.RCAName> {item?.nome_usuario} </S.RCAName>
                                    <S.RCADescription>
                                        {`Cod Loja - ${item?.cod_loja} | Cod Pessoa - ${item?.cod_pessoa}`}
                                    </S.RCADescription>
                                </S.RCABottomInfo>
                            </S.RCACard>
                        ))}
                    </S.ContentListCompany>
                </S.ModalContent>

                <S.BottomContent>
                    <S.ButtonAccess onClick={handleStartLogin} disabled={!selectedRCA || isLoading}>
                        {isLoading ? "Entrando..." : "Entrar como Representante Comercial"}
                    </S.ButtonAccess>
                </S.BottomContent>
            </Modal>
        </>
    );
};

export default CoordinatorModal;
